import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ArrowUpRight, MapPin, Building2, Calendar, Pencil, UserPlus, ShieldOff, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

const PROJECT_FIELDS = [
  ["name", "Project Name"],
  ["number", "Project Number"],
  ["client", "Client"],
  ["contractor", "Contractor"],
  ["start_date", "Start Date"],
  ["end_date", "End Date"],
];

function emptyForm() {
  return { name: "", number: "", client: "", contractor: "", start_date: "", end_date: "", notes: "" };
}

function ProjectFormFields({ form, setForm, prefix = "project-field" }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {PROJECT_FIELDS.map(([k, l]) => (
        <div key={k}>
          <Label className="text-xs">{l}</Label>
          <Input
            type={k.endsWith("date") ? "date" : "text"}
            value={form[k] || ""}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            data-testid={`${prefix}-${k}`}
          />
        </div>
      ))}
      <div className="md:col-span-2">
        <Label className="text-xs">Notes</Label>
        <Textarea
          value={form.notes || ""}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={3}
          data-testid={`${prefix}-notes`}
        />
      </div>
    </div>
  );
}

export function ProjectsRegister() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const reload = () => api.get("/projects").then(r => setProjects(r.data));
  useEffect(() => { reload(); }, []);

  const create = async () => {
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      setOpen(false);
      setForm(emptyForm());
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="projects-page">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="overline mb-1">Register</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">Projects</h1>
          <p className="mt-1 text-sm text-slate-600">All active and completed construction projects.</p>
        </div>
        {user?.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-700 hover:bg-teal-800" data-testid="create-project-btn">
                <Plus className="mr-2 size-4" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Create Project</DialogTitle></DialogHeader>
              <ProjectFormFields form={form} setForm={setForm} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="project-cancel">Cancel</Button>
                <Button onClick={create} className="bg-teal-700 hover:bg-teal-800" data-testid="project-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {projects.map((p) => (
          <Card key={p.id} className="border-slate-200 p-5 transition-all hover:shadow-md" data-testid={`project-card-${p.number}`}>
            <Link to={`/p/${p.id}/dashboard`} className="block" data-testid={`enter-project-${p.number}`}>
              <div className="mb-2 flex items-start justify-between gap-2">
                <div>
                  <div className="overline text-teal-700">{p.number}</div>
                  <h3 className="mt-1 font-heading text-lg font-bold tracking-tight text-slate-900">{p.name}</h3>
                </div>
                <span className="rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-orange-700">Open →</span>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                <div className="flex items-center gap-2"><Building2 className="size-3.5" />{p.client} · {p.contractor}</div>
                <div className="flex items-center gap-2"><Calendar className="size-3.5" />{p.start_date || "—"} → {p.end_date || "—"}</div>
              </div>
            </Link>
            <div className="mt-2 border-t border-slate-100 pt-2 text-right">
              <Link to={`/projects/${p.id}`} className="text-xs text-slate-500 hover:text-teal-700" data-testid={`project-settings-${p.number}`}>Project settings →</Link>
            </div>
          </Card>
        ))}
        {projects.length === 0 && <div className="text-slate-500">No projects yet.</div>}
      </div>
    </div>
  );
}

export function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [lots, setLots] = useState([]);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const reload = () => api.get(`/projects/${id}`).then(r => { setP(r.data); setForm({ ...emptyForm(), ...r.data }); });

  useEffect(() => {
    reload();
    api.get("/lots", { params: { project_id: id } }).then(r => setLots(r.data));
  }, [id]);

  const save = async () => {
    try {
      const body = {};
      for (const k of ["name","number","client","contractor","start_date","end_date","notes"]) {
        body[k] = form[k] || null;
      }
      await api.put(`/projects/${id}`, body);
      toast.success("Project updated");
      setEdit(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!p) return <div className="p-8 text-slate-500">Loading…</div>;
  return (
    <div className="p-4 md:p-8" data-testid="project-detail-page">
      <Link to="/projects" className="text-sm text-slate-500 hover:underline">← Projects</Link>
      <div className="mt-3 flex items-start justify-between gap-4">
        <div>
          <div className="overline text-teal-700">{p.number}</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">{p.name}</h1>
        </div>
        {user?.role === "admin" && (
          <Button onClick={() => setEdit(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="edit-project-btn">
            <Pencil className="mr-1.5 size-4"/>Edit Project
          </Button>
        )}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        {[
          ["Client", p.client], ["Contractor", p.contractor],
          ["Start", p.start_date || "—"], ["End", p.end_date || "—"],
        ].map(([l,v]) => (
          <Card key={l} className="border-slate-200 p-4">
            <div className="overline">{l}</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{v || "—"}</div>
          </Card>
        ))}
        {p.notes && (
          <Card className="border-slate-200 p-4 md:col-span-3">
            <div className="overline">Notes</div>
            <div className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{p.notes}</div>
          </Card>
        )}
      </div>
      <h2 className="mt-8 font-heading text-xl font-bold tracking-tight">Project Lots ({lots.length})</h2>
      <Card className="mt-3 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
            <tr><th className="px-4 py-3">Lot</th><th className="px-4 py-3">Activity</th><th className="px-4 py-3">Status</th><th></th></tr>
          </thead>
          <tbody>
            {lots.map(l => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono font-semibold">{l.lot_number}</td>
                <td className="px-4 py-3">{l.activity}</td>
                <td className="px-4 py-3">{l.status}</td>
                <td className="px-4 py-3"><Link to={`/lots/${l.id}`} className="text-teal-700">Open</Link></td>
              </tr>
            ))}
            {lots.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No lots yet</td></tr>}
          </tbody>
        </table>
      </Card>

      {["admin", "qa_manager"].includes(user?.role) && (
        <ClientReviewerPool projectId={id} />
      )}

      <Dialog open={edit} onOpenChange={setEdit}>
        <DialogContent className="max-w-2xl" data-testid="edit-project-dialog">
          <DialogHeader><DialogTitle>Edit Project</DialogTitle></DialogHeader>
          <ProjectFormFields form={form} setForm={setForm} prefix="project-edit-field" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(false)}>Cancel</Button>
            <Button onClick={save} className="bg-teal-700 hover:bg-teal-800" data-testid="project-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ClientReviewerPool({ projectId }) {
  const [reviewers, setReviewers] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", company: "", role_title: "" });
  const [saving, setSaving] = useState(false);

  const reload = () => {
    api.get(`/projects/${projectId}/client-reviewers`, { params: { include_inactive: includeInactive } })
      .then((r) => setReviewers(r.data || []))
      .catch(() => toast.error("Failed to load reviewers"));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId, includeInactive]);

  const add = async () => {
    if (!form.email) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/client-reviewers`, form);
      toast.success("Reviewer linked to project");
      setOpen(false);
      setForm({ email: "", name: "", company: "", role_title: "" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (uid, email) => {
    if (!window.confirm(`Remove ${email} from this project's Client Reviewer pool?`)) return;
    try {
      await api.delete(`/projects/${projectId}/client-reviewers/${uid}`);
      toast.success("Reviewer removed from project");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="mt-10" data-testid="client-reviewer-pool">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2"><UsersIcon className="size-5 text-teal-700" />Client Reviewer Pool</h2>
          <p className="mt-1 text-xs text-slate-600">Add authorised Client Reviewers (Surveillance Officer, PM, Superintendent Rep, etc.) so HP/AP requests can keep moving when one reviewer is unavailable.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1 text-xs text-slate-600">
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid="show-inactive-reviewers"/>
            Show inactive
          </label>
          <Button onClick={() => setOpen(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="add-client-reviewer-btn">
            <UserPlus className="mr-2 size-4"/>Add Client Reviewer
          </Button>
        </div>
      </div>
      <Card className="mt-3 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Role / Title</th>
              <th className="px-4 py-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {reviewers.length === 0 && (<tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No Client Reviewers in this project&apos;s pool yet.</td></tr>)}
            {reviewers.map(r => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`reviewer-pool-row-${r.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-900">{r.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.email}</td>
                <td className="px-4 py-3 text-slate-700">{r.company || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-slate-700">{r.role_title || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3">{r.is_active === false ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Inactive</span> : <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">Active</span>}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id, r.email)} data-testid={`remove-reviewer-${r.id}`}><ShieldOff className="mr-1 size-3.5"/>Remove from project</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="add-reviewer-dialog">
          <DialogHeader><DialogTitle>Add Client Reviewer to this project</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-600">If a Client Reviewer with this email already exists, they will be linked to this project (no duplicate account is created).</p>
          <div className="mt-2 space-y-3">
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reviewer-email-input"/></div>
            <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reviewer-name-input"/></div>
            <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="reviewer-company-input"/></div>
            <div><Label>Role / Title</Label><Input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} placeholder="Surveillance Officer / PM / Superintendent Rep / Client Engineer" data-testid="reviewer-role-title-input"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={add} disabled={saving || !form.email} className="bg-teal-700 hover:bg-teal-800" data-testid="save-reviewer-btn">{saving ? "Saving…" : "Add to project"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
